import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import "./CommissionWorkspace.css";

const money = (value) => Number(value || 0).toFixed(2);

function CommissionWorkspace() {
  const [register, setRegister] = useState([]);
  const [methods, setMethods] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("entries");
  const [paying, setPaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const searchTimer = useRef(null);
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "",
    reference: "",
    notes: "",
    allocations: {},
  });

  const load = async () => {
    const response = await axiosInstance.get("commissions/", {
      params: search.trim() ? { search: search.trim() } : {},
    });
    setRegister(response.data.sales_people || []);
    setMethods(response.data.payment_methods || []);
  };

  const open = async (person) => {
    setSelected(person);
    const response = await axiosInstance.get(
      `commissions/sales-person/${person.id}/`
    );
    setDetail(response.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load commissions."));
  }, []);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      load().catch(() => {});
    }, search.trim() ? 220 : 0);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search]);

  const outstanding = detail?.outstanding_entries || [];
  const allocatedTotal = useMemo(
    () =>
      Object.values(form.allocations).reduce(
        (total, amount) => total + Number(amount || 0),
        0
      ),
    [form.allocations]
  );

  const allocateBalance = (entry) => {
    setForm({
      ...form,
      allocations: {
        ...form.allocations,
        [entry.id]: entry.balance,
      },
    });
  };

  const allocateAll = () => {
    const allocations = {};
    outstanding.forEach((entry) => {
      allocations[entry.id] = entry.balance;
    });
    setForm({ ...form, allocations });
  };

  const startPayment = () => {
    if (!outstanding.length) {
      toast.info("This salesperson has no outstanding commission.");
      return;
    }
    setForm({
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: String(methods[0]?.id || ""),
      reference: "",
      notes: "",
      allocations: {},
    });
    setPaying(true);
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    const allocations = Object.entries(form.allocations)
      .filter(([, amount]) => Number(amount || 0) > 0)
      .map(([commission_id, amount]) => ({
        commission_id: Number(commission_id),
        amount,
      }));
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        `commissions/sales-person/${selected.id}/pay/`,
        { ...form, allocations }
      );
      toast.success(response.data.detail);
      setPaying(false);
      await load();
      await open(selected);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Commission payment could not be completed."
      );
    } finally {
      setBusy(false);
    }
  };

  const reconcile = async () => {
    setBusy(true);
    try {
      const response = await axiosInstance.post(
        "commissions/reconcile/",
        {}
      );
      toast.success(response.data.detail);
      await load();
      if (selected) await open(selected);
    } catch {
      toast.error("Commission reconciliation failed.");
    } finally {
      setBusy(false);
    }
  };

  const reversePayment = async (payment) => {
    const reason = window.prompt(
      `State the exact reason for reversing ${payment.code}:`
    );
    if (!reason) return;
    try {
      const response = await axiosInstance.post(
        `commissions/payments/${payment.id}/reverse/`,
        { reason }
      );
      toast.success(response.data.detail);
      await load();
      await open(selected);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Commission payment reversal failed."
      );
    }
  };

  return (
    <section className="commission-page">
      <header className="commission-title">
        <div>
          <span>ASSURANCE FINANCE</span>
          <h1>Commissions</h1>
          <p>
            Salesperson commission earnings, allocations and payment history.
          </p>
        </div>
        <button onClick={reconcile} disabled={busy}>
          <i className="bi bi-arrow-repeat" />
          {busy ? "Reconciling..." : "Reconcile totals"}
        </button>
      </header>

      <div className="commission-search">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && load()}
          placeholder="Salesperson code, first name or surname"
        />
        <button onClick={load}>Search</button>
      </div>

      <div className="commission-layout">
        <aside className="commission-people">
          {register.map((person) => (
            <button
              key={person.id}
              className={selected?.id === person.id ? "active" : ""}
              onClick={() => open(person)}
            >
              <div>
                <strong>{person.code}</strong>
                <span>{person.full_name}</span>
                <small>{person.branch}</small>
              </div>
              <div className="commission-person-money">
                <span>Total {money(person.total_commission)}</span>
                <span>Paid {money(person.paid_commission)}</span>
                <b>Balance {money(person.commission_balance)}</b>
              </div>
            </button>
          ))}
        </aside>

        <main className="commission-detail">
          {!detail ? (
            <div className="commission-empty">
              Select a salesperson to review commissions.
            </div>
          ) : (
            <>
              <header className="commission-person-head">
                <div>
                  <span>{detail.sales_person.code}</span>
                  <h2>{detail.sales_person.full_name}</h2>
                  <p>{detail.sales_person.branch}</p>
                </div>
                <button onClick={startPayment}>
                  <i className="bi bi-cash-coin" />
                  Pay commission
                </button>
              </header>

              <div className="commission-metrics">
                <article>
                  <span>Total commission</span>
                  <strong>{money(detail.sales_person.total_commission)}</strong>
                </article>
                <article>
                  <span>Paid commission</span>
                  <strong>{money(detail.sales_person.paid_commission)}</strong>
                </article>
                <article>
                  <span>Commission balance</span>
                  <strong>{money(detail.sales_person.commission_balance)}</strong>
                </article>
              </div>

              <div className="commission-tabs">
                <button
                  className={tab === "entries" ? "active" : ""}
                  onClick={() => setTab("entries")}
                >
                  Commission entries
                </button>
                <button
                  className={tab === "payments" ? "active" : ""}
                  onClick={() => setTab("payments")}
                >
                  Payment history
                </button>
              </div>

              <div className="commission-table">
                {tab === "entries" ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Premium payment</th>
                        <th>Date</th>
                        <th>Policy</th>
                        <th>Rate</th>
                        <th>Commission</th>
                        <th>Paid</th>
                        <th>Balance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.entries.map((entry) => (
                        <tr key={entry.id}>
                          <td>{entry.payment_code}</td>
                          <td>{entry.payment_date}</td>
                          <td>{entry.policy_number || "—"}</td>
                          <td>{entry.percentage}%</td>
                          <td>{money(entry.commission_amount)}</td>
                          <td>{money(entry.amount_paid)}</td>
                          <td>{money(entry.balance)}</td>
                          <td>{entry.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Payment</th>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Reference</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {detail.payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>{payment.code}</td>
                          <td>{payment.payment_date}</td>
                          <td>{payment.payment_method}</td>
                          <td>{payment.reference || "—"}</td>
                          <td>{money(payment.total_amount)}</td>
                          <td>{payment.status}</td>
                          <td>
                            {payment.status !== "REVERSED" && (
                              <button
                                className="table-action"
                                onClick={() => reversePayment(payment)}
                              >
                                Reverse
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {paying && (
        <div className="commission-modal">
          <form onSubmit={submitPayment}>
            <header>
              <div>
                <span>COMMISSION PAYMENT</span>
                <h2>{detail.sales_person.full_name}</h2>
                <p>
                  Allocate one payment across the selected outstanding
                  commission entries.
                </p>
              </div>
              <button type="button" onClick={() => setPaying(false)}>×</button>
            </header>

            <div className="commission-payment-fields">
              <label>
                Payment date
                <input
                  type="date"
                  required
                  value={form.payment_date}
                  onChange={(event) =>
                    setForm({ ...form, payment_date: event.target.value })
                  }
                />
              </label>
              <label>
                Payment method
                <select
                  required
                  value={form.payment_method}
                  onChange={(event) =>
                    setForm({ ...form, payment_method: event.target.value })
                  }
                >
                  <option value="">SELECT</option>
                  {methods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Reference
                <input
                  value={form.reference}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      reference: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>
              <button type="button" className="allocate-all" onClick={allocateAll}>
                Allocate all balances
              </button>
            </div>

            <div className="commission-allocation-table">
              <table>
                <thead>
                  <tr>
                    <th>Premium payment</th>
                    <th>Date</th>
                    <th>Rate</th>
                    <th>Commission</th>
                    <th>Balance</th>
                    <th>Payment amount</th>
                  </tr>
                </thead>
                <tbody>
                  {outstanding.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.payment_code}</td>
                      <td>{entry.payment_date}</td>
                      <td>{entry.percentage}%</td>
                      <td>{money(entry.commission_amount)}</td>
                      <td>{money(entry.balance)}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          max={entry.balance}
                          step="0.01"
                          value={form.allocations[entry.id] || ""}
                          onDoubleClick={() => allocateBalance(entry)}
                          onChange={(event) =>
                            setForm({
                              ...form,
                              allocations: {
                                ...form.allocations,
                                [entry.id]: event.target.value,
                              },
                            })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <label className="commission-notes">
              Notes
              <textarea
                rows="2"
                value={form.notes}
                onChange={(event) =>
                  setForm({
                    ...form,
                    notes: event.target.value.toUpperCase(),
                  })
                }
              />
            </label>

            <footer>
              <strong>Total payment: {money(allocatedTotal)}</strong>
              <div>
                <button type="button" onClick={() => setPaying(false)}>
                  Cancel
                </button>
                <button className="primary" disabled={busy || allocatedTotal <= 0}>
                  {busy ? "Processing..." : "Complete payment"}
                </button>
              </div>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}

export default CommissionWorkspace;
